// Pantalla principal: métricas, presentaciones disponibles, alertas activas,
// agotados/stock bajo y últimos movimientos. Port de views/dashboard.js.
import { Link } from '@tanstack/react-router';
import { useDashboard } from '../hooks/useDashboard';
import { StatCard } from '../components/StatCard';
import { Badge, NivelBadge, TipoBadge } from '../components/Badge';
import { DataTable, type Column } from '../components/DataTable';
import { SkeletonCards, SkeletonTable } from '../components/Skeletons';
import { ErrorBox } from '../components/ErrorBox';
import { fechaBogota, horaBogota } from '../lib/fecha';
import type { Movimiento, Alerta } from '../types';

function pct(a: number, b: number) {
  return b ? Math.round((a / b) * 100) : 0;
}

function MiniChart({ data }: { data: number[] }) {
  const puntos = data.length ? data : [3, 5, 4, 6, 5, 7, 6, 8];
  const w = 130, h = 44;
  const max = Math.max(...puntos), min = Math.min(...puntos);
  const rng = max - min || 1;
  const pts = puntos.map((v, i) => `${(i / (puntos.length - 1 || 1)) * w},${h - ((v - min) / rng) * (h - 6) - 3}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} fill="none">
      <polyline points={pts} stroke="var(--success)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={`0,${h} ${pts} ${w},${h}`} fill="var(--success-soft)" stroke="none" opacity=".5" />
    </svg>
  );
}

export function Dashboard() {
  const { data, isLoading, isError, error } = useDashboard();

  if (isLoading) {
    return (
      <>
        <div className="page-head"><h2>Resumen general</h2></div>
        <SkeletonCards n={4} />
        <div style={{ height: 20 }} />
        <SkeletonTable rows={5} />
      </>
    );
  }
  if (isError || !data) {
    return (
      <>
        <div className="page-head"><h2>Resumen general</h2></div>
        <ErrorBox mensaje={(error as Error)?.message ?? 'Error desconocido'} />
      </>
    );
  }

  const t = data.totales;
  const pendientes = t.stockBajo + t.agotados;
  const spark = data.movimientosRecientes.map((_, i) => 4 + ((i * 7) % 11));

  const cols: Column<Movimiento>[] = [
    { key: 'created_at', label: 'Fecha', render: (r) => (<>{fechaBogota(r.created_at)}<br /><small className="text-muted">{horaBogota(r.created_at)}</small></>) },
    { key: 'tipo', label: 'Tipo', render: (r) => <TipoBadge tipo={r.tipo} /> },
    { key: 'item_nombre', label: 'Ítem', render: (r) => r.item_nombre || '—' },
    { key: 'cantidad', label: 'Cant.', render: (r) => (r.cantidad != null ? <b>{r.cantidad}</b> : '—') },
    { key: 'descripcion', label: 'Detalle', render: (r) => <small className="text-muted">{r.descripcion}</small> },
  ];

  return (
    <>
      <div className="page-head">
        <h2>Resumen general</h2>
        <Link to="/alertas" className="btn outline sm"><i className="fa-solid fa-bell" /> Ver alertas</Link>
      </div>

      {pendientes ? (
        <div className="banner">
          <span className="chip"><i className="fa-solid fa-bell" /></span>
          <div>
            <b>Pendientes por resolver</b> <Badge type="warning" dot={false}>{pendientes}</Badge>
            <br /><small>Revisa el stock bajo o agotado para mantener tus bodegas al día.</small>
          </div>
        </div>
      ) : null}

      <div className="grid cols-4">
        <StatCard icon="fa-warehouse" color="primary" label="Bodegas" value={t.bodegas} desc="Activas en el sistema" />
        <StatCard icon="fa-box" color="info" label="Productos" value={t.productos} desc="Registrados" />
        <StatCard icon="fa-boxes-stacked" color="success" label="Presentaciones" value={t.presentaciones} desc="Definidas" />
        <StatCard icon="fa-clock-rotate-left" color="warning" label="Movimientos" value={t.movimientos} desc="Historial total" />
      </div>

      <div className="dash-hero" style={{ marginTop: 'var(--gap)' }}>
        <div className="card accent-top">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="stat">
              <span className="chip c-success"><i className="fa-solid fa-boxes-stacked" /></span>
              <span className="stat-label">Presentaciones disponibles</span>
              <span className="stat-value">{t.presentacionesDisponibles}</span>
              <span><Badge type="success">Unidades fabricadas</Badge></span>
            </div>
            <div className="mini-chart"><MiniChart data={spark} /></div>
          </div>
        </div>

        <div className="card b-alertas">
          <div className="card-head"><span className="chip c-danger"><i className="fa-solid fa-triangle-exclamation" /></span><h3>Alertas activas</h3></div>
          <div>
            {data.alertas.length ? (
              data.alertas.map((a: Alerta) => (
                <div className="row" key={`${a.bodega_id}-${a.item_id}`} style={{ padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                  <span className={`chip c-${a.nivel === 'error' ? 'danger' : 'warning'}`} style={{ width: 32, height: 32, fontSize: 13 }}>
                    <i className={`fa-solid ${a.nivel === 'error' ? 'fa-ban' : 'fa-triangle-exclamation'}`} />
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{a.item_nombre}</div>
                    <small className="text-muted">{a.bodega || ''}</small>
                  </div>
                  <NivelBadge nivel={a.tipo} />
                </div>
              ))
            ) : (
              <div className="empty-state"><i className="fa-solid fa-circle-check" style={{ color: 'var(--success)' }} />Todo en orden. Sin alertas.</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid cols-2" style={{ marginTop: 'var(--gap)' }}>
        <div className="card b-agotados">
          <div className="card-head">
            <span className="chip c-danger"><i className="fa-solid fa-ban" /></span>
            <div><h3>Agotados</h3><small className="text-muted">Sin existencias</small></div>
            <span className="spacer" /><Badge type="danger">{pct(t.agotados, t.productos)}%</Badge>
          </div>
          <div className="stat-value" style={{ fontSize: 26 }}>{t.agotados} <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>ítem(s)</span></div>
          <div className="progress"><span style={{ width: `${pct(t.agotados, t.productos)}%`, background: 'var(--danger)' }} /></div>
        </div>
        <div className="card b-bajo">
          <div className="card-head">
            <span className="chip c-warning"><i className="fa-solid fa-arrow-trend-down" /></span>
            <div><h3>Stock bajo</h3><small className="text-muted">Por debajo del mínimo</small></div>
            <span className="spacer" /><Badge type="warning">{pct(t.stockBajo, t.productos)}%</Badge>
          </div>
          <div className="stat-value" style={{ fontSize: 26 }}>{t.stockBajo} <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>ítem(s)</span></div>
          <div className="progress"><span style={{ width: `${pct(t.stockBajo, t.productos)}%`, background: 'var(--warning)' }} /></div>
        </div>
      </div>

      <div className="section-title" style={{ marginTop: 26 }}>Movimientos recientes</div>
      <DataTable title="Últimas operaciones" search={false} pageSize={8} cols={cols} rows={data.movimientosRecientes} empty="Aún no hay movimientos." />
    </>
  );
}
